import React, { useMemo, useState, createContext, useContext } from 'react'
import useTranslation from '../../components/shared/TranslationProvider'

const TemplateContext = createContext()

export const useTemplate = () => useContext(TemplateContext)

export const TemplateProvider = ({
  templates,
  defaultStyle,
  templateSupportedStyles,
  template_index,
  dev_mode,
  locale,
  children,
}) => {
  const { t } = useTranslation('TemplateProvider')

  // Override defaultStyle from templates with translations
  const translatedDefaultStyle = useMemo(() => {
    const translated = { ...defaultStyle }
    Object.keys(defaultStyle).forEach((templateKey, idx) => {
      const style = defaultStyle[templateKey]
      translated[templateKey] = { ...style }
      Object.keys(style).forEach((key) => {
        const translation = t(`Style${idx + 1}.${key}`)
        if (translation && !translation.includes('missing')) {
          translated[templateKey][key] = translation
        }
      })
    })
    return translated
  }, [defaultStyle, t])

  // Use translatedDefaultStyle instead of defaultStyle below
  const [templateSettings, setTemplateSettings] = useState(
    translatedDefaultStyle,
  )
  const [templateIndex, setTemplateIndex] = useState(template_index || 0)
  const [showAllEditorFields, setShowAllEditorFields] = useState(false)

  const currentTemplateKey = useMemo(
    () => `template-${templateIndex}`,
    [templateIndex],
  )

  const templateStyle = useMemo(() => {
    const currentTemplateSettings = templateSettings[currentTemplateKey] || {}
    return {
      ...defaultStyle[currentTemplateKey],
      ...currentTemplateSettings,
    }
  }, [templateSettings, defaultStyle, currentTemplateKey])

  const setTemplateSetting = (setting, value) => {
    const innerTemplateSettings = templateSettings[currentTemplateKey]
    const newTemplateSettings = {
      ...templateSettings,
      [currentTemplateKey]: {
        ...innerTemplateSettings,
        [setting]: value,
      },
    }
    setTemplateSettings(newTemplateSettings)
  }

  const getTemplateSetting = (setting) => {
    if (!templateSettings || !templateSettings[currentTemplateKey]) {
      return null
    }
    return templateSettings[currentTemplateKey][setting]
  }

  const useTemplateSetting = (setting) => [
    getTemplateSetting(setting),
    (value) => setTemplateSetting(setting, value),
  ]

  const isSettingSupported = (setting) => {
    if (
      !templateSupportedStyles ||
      !templateSupportedStyles[currentTemplateKey]
    ) {
      return false
    }
    return templateSupportedStyles[currentTemplateKey].includes(setting)
  }

  const editorFieldEnabled = (fieldName) => {
    if (showAllEditorFields) return true

    const currentTemplate = templates[templateIndex]
    if (!currentTemplate.getOptionalEditorFields(locale)) {
      console.warn(
        `Template${templateIndex} does not define getOptionalEditorFields`,
      )
      return false
    }
    if (
      !Object.keys(currentTemplate.getOptionalEditorFields(locale)).includes(
        fieldName,
      )
    ) {
      console.warn(
        `Field ${fieldName} is not defined in Template${templateIndex}`,
      )
      return false
    }
    return currentTemplate.getOptionalEditorFields(locale)[fieldName]
  }

  const value = {
    templateIndex,
    setTemplateIndex,
    templateSettings,
    useTemplateSetting,
    templateStyle,
    templates,
    isSettingSupported,
    editorFieldEnabled,
    showAllEditorFields,
    setShowAllEditorFields,
    devMode: dev_mode,
    locale,
  }

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  )
}
export default TemplateProvider
